"use client";

import { useState } from "react";
import Image from "next/image";
import { Button } from "@/components/ui/button";
import { Star } from "lucide-react";
import type { ReviewData } from "@/types/exam-detail";

/**
 * Daftar ulasan — daun detail ujian MILIK zona landing (duplikasi penuh per-zona
 * 2026-09-26; pasangan antar-zona: `(user)/ujianku/[slug]/_components/exam-detail-reviews.tsx`). Eks satu file shared
 * `exam-detail-interactive` di kolam global.
 */
/** Daftar ulasan + toggle "Lihat semua / Sembunyikan" (default 5 teratas). */
export function ExamDetailReviews({ reviews }: { reviews: ReviewData }) {
  const [showAllReviews, setShowAllReviews] = useState(false);

  if (reviews.reviews.length === 0) {
    return (
      <p className="text-foreground/40 mt-3 text-sm">Belum ada ulasan untuk ujian ini.</p>
    );
  }

  return (
    <>
      <div className="mt-4 flex flex-col gap-3">
        {(showAllReviews ? reviews.reviews : reviews.reviews.slice(0, 5)).map(
          (review) => (
            <div
              key={review.id}
              className="border-border/70 bg-card rounded-2xl border p-4"
            >
              <div className="flex items-center gap-3">
                <div className="bg-primary/10 text-primary relative flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full text-sm font-black">
                  {review.avatarUrl ? (
                    <Image
                      src={review.avatarUrl}
                      alt=""
                      fill
                      sizes="40px"
                      className="h-full w-full rounded-full object-cover"
                    />
                  ) : (
                    review.userName.charAt(0).toUpperCase()
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-foreground truncate text-sm font-bold">
                    {review.userName}
                  </p>
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <Star
                        key={s}
                        size={11}
                        className={
                          s <= review.rating
                            ? "fill-secondary text-secondary"
                            : "text-foreground/15"
                        }
                      />
                    ))}
                    <span className="text-foreground/30 text-3xs ml-1.5">
                      {new Date(review.createdAt).toLocaleDateString("id-ID", {
                        day: "numeric",
                        month: "short",
                        year: "numeric",
                      })}
                    </span>
                  </div>
                </div>
              </div>
              {review.comment && (
                <p className="text-foreground/60 mt-2.5 text-sm leading-relaxed">
                  {review.comment}
                </p>
              )}
            </div>
          ),
        )}
      </div>
      {reviews.reviews.length > 5 && (
        <Button
          type="button"
          variant="outline"
          size="lg"
          onClick={() => setShowAllReviews((v) => !v)}
          className="mt-3 w-full"
        >
          {showAllReviews
            ? "Sembunyikan ulasan"
            : `Lihat semua ulasan (${reviews.reviews.length})`}
        </Button>
      )}
    </>
  );
}
